import { Fragment } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AppSettingsProvider } from '@/contexts/AppSettingsContext';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { UndoProvider } from '@/contexts/UndoContext';
import { Layout } from '@/components/Layout';
import { Toaster } from '@/components/ui/sonner';
import { ConfirmProvider } from '@/components/ui/confirm-dialog';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import ProjectsPage from '@/pages/ProjectsPage';
import ProjectDetailPage from '@/pages/ProjectDetailPage';
import TasksPage from '@/pages/TasksPage';
import ShopDrawingsPage from '@/pages/ShopDrawingsPage';
import DocumentsPage from '@/pages/DocumentsPage';
import InventoryPage from '@/pages/InventoryPage';
import ProcurementPage from '@/pages/ProcurementPage';
import ForecastPage from '@/pages/ForecastPage';
import InvoicingPage from '@/pages/InvoicingPage';
import AiPage from '@/pages/AiPage';
import AdminPage from '@/pages/AdminPage';
import '@/App.css';
import { hasPermission } from '@/lib/permissions';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Lock } from 'lucide-react';
import { usePaletteNavKey } from '@/hooks/usePaletteNav';

// Pages in sidebar order with the permission that opens each (the same as the routes below).
const PAGE_PERMISSIONS = [
  ['/', 'dashboard'],
  ['/tasks', 'tasks'],
  ['/projects', 'projects'],
  ['/shop-drawings', 'shop_drawings'],
  ['/documents', 'files'],
  ['/inventory', 'inventory'],
  ['/procurement', 'procurement'],
  ['/invoicing', 'invoicing'],
  ['/forecast', 'forecast'],
  ['/ai', 'ai'],
  ['/admin', 'admin'],
];

/** The first page this user may open ('/' when they have the Dashboard), or null when none. */
export const firstAllowedPath = (user) => PAGE_PERMISSIONS.find(([, permission]) => hasPermission(user, permission))?.[0] || null;

function ProtectedRoute({ children, roles, permission }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  // Changes only when the command palette opens a link on the page that is already open: the page remounts and
  // reads its deep link (?lpo=, ?tab=stock&search=…) like on a fresh load. See hooks/usePaletteNav.js.
  const pageKey = usePaletteNavKey();
  if (loading) return (
    <div className="app-theme-background flex min-h-[100dvh] items-center justify-center p-8" data-testid="app-loading">
      <div className="w-full max-w-md space-y-4">
        <Skeleton className="h-10 w-48 rounded-xl" />
        <Skeleton className="h-4 w-full rounded-lg" />
        <Skeleton className="h-4 w-5/6 rounded-lg" />
        <Skeleton className="h-28 w-full rounded-2xl" />
      </div>
    </div>
  );
  if (!user) return <Navigate to="/login" />;
  if ((roles && !roles.includes(user.role)) || (permission && !hasPermission(user, permission))) {
    // Send the person to a page they CAN open. "/" would redirect to itself (a blank page) for a role
    // without the Dashboard permission (02 problem #6), so go to their first allowed page instead.
    const fallback = firstAllowedPath(user);
    if (fallback && fallback !== pathname) return <Navigate to={fallback} replace />;
    return (
      <Layout>
        <EmptyState
          icon={Lock}
          titleAs="h1"
          title="No pages are open to your role yet"
          text="Ask an administrator to give your role access in Admin › Access."
        />
      </Layout>
    );
  }
  return <Layout><Fragment key={pageKey}>{children}</Fragment></Layout>;
}

function App() {
  return (
    <AppSettingsProvider>
      <AuthProvider>
        <UndoProvider>
          <BrowserRouter>
            <ConfirmProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/" element={<ProtectedRoute permission="dashboard"><DashboardPage /></ProtectedRoute>} />
                <Route path="/projects" element={<ProtectedRoute permission="projects"><ProjectsPage /></ProtectedRoute>} />
                <Route path="/projects/:id" element={<ProtectedRoute permission="projects"><ProjectDetailPage /></ProtectedRoute>} />
                <Route path="/tasks" element={<ProtectedRoute permission="tasks"><TasksPage /></ProtectedRoute>} />
                <Route path="/shop-drawings" element={<ProtectedRoute permission="shop_drawings"><ShopDrawingsPage /></ProtectedRoute>} />
                <Route path="/documents" element={<ProtectedRoute permission="files"><DocumentsPage /></ProtectedRoute>} />
                <Route path="/inventory" element={<ProtectedRoute permission="inventory"><InventoryPage /></ProtectedRoute>} />
                <Route path="/procurement" element={<ProtectedRoute permission="procurement"><ProcurementPage /></ProtectedRoute>} />
                <Route path="/forecast" element={<ProtectedRoute permission="forecast"><ForecastPage /></ProtectedRoute>} />
                <Route path="/invoicing" element={<ProtectedRoute permission="invoicing"><InvoicingPage /></ProtectedRoute>} />
                <Route path="/ai" element={<ProtectedRoute permission="ai"><AiPage /></ProtectedRoute>} />
                <Route path="/admin" element={<ProtectedRoute permission="admin"><AdminPage /></ProtectedRoute>} />
                {/* a mistyped or retired address lands on the start page instead of a blank screen */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ConfirmProvider>
            {/* Feedback toasts (incl. notify() with Undo) dock bottom-right — see components/ui/sonner.jsx */}
            <Toaster />
          </BrowserRouter>
        </UndoProvider>
      </AuthProvider>
    </AppSettingsProvider>
  );
}

export default App;
